import { TOOLBAR_POPUP_CLASS_NAME } from '@components/App/Toolbar'
import { DropdownContainer, Menu } from '@lib/components'
import { Button, ThemeColors } from '@octopus-do/octopus-storybook'
import {
  Lock,
  Palette,
  Sliders,
  // biome-ignore lint/suspicious/noShadowRestrictedNames: Symbol is a domain component name
  Symbol,
  UserFriends,
} from '@octopus-do/octopus-storybook/icons'
import type React from 'react'

import { clsJoin } from '@/utils'
import { SettingsButton } from '../SettingsButton'
import css from './styles.module.css'
import { useSettingsDropdown } from './useSettingsDropdown'

// The button has room for one icon: a password closes the project tighter
// than members-only access, so the lock wins when both are on.
const getShareIcon = (hasPassword: boolean, onlyForMembers: boolean) => {
  if (hasPassword) {
    return Lock
  }

  if (onlyForMembers) {
    return UserFriends
  }

  return undefined
}

export const SettingsDropdown: React.FC = () => {
  const {
    hasNotAvailableAccessShare,
    hasPassword,
    projectIsOnlyForMembers,
    hasNotAvailableAccessBadge,
    showShare,
    showPublish,
    onShareClick,
    onPublishClick,
    handleClickSettings,
    handleClickColors,
    handleClickSymbols,
    handleBadgeClick,
  } = useSettingsDropdown()

  return (
    <DropdownContainer
      position="bottom"
      offset={{ x: -31 }}
      customClassName={clsJoin(ThemeColors.light, TOOLBAR_POPUP_CLASS_NAME)}
      renderButton={(onSetRef, onClick, isOpen) => (
        <SettingsButton ref={onSetRef} active={isOpen} onClick={onClick} />
      )}
    >
      {(closeDropdown) => (
        <>
          {showShare && (
            <div className={css.button_wrap}>
              <Button
                className={css.button}
                size="small"
                label="Share"
                right={getShareIcon(hasPassword, projectIsOnlyForMembers)}
                disabled={hasNotAvailableAccessShare}
                onClick={() => {
                  onShareClick()
                  closeDropdown()
                }}
              />
            </div>
          )}
          {showPublish && (
            <div className={css.button_wrap}>
              <Button
                className={css.button}
                priority="secondary"
                size="small"
                label="Publish"
                onClick={() => {
                  onPublishClick()
                  closeDropdown()
                }}
              />
            </div>
          )}

          {(showShare || showPublish) && <div className={css.divider} />}

          <Menu
            onClick={() => {
              handleClickSettings()
              closeDropdown()
            }}
          >
            <div className={css.menu_inner}>
              <Sliders />
              Project settings
              <div />
            </div>
          </Menu>
          <Menu
            disabled={hasNotAvailableAccessBadge}
            notAdvanced={hasNotAvailableAccessBadge}
            {...(hasNotAvailableAccessBadge && {
              onProBadgeClick: handleBadgeClick,
            })}
            onClick={() => handleClickColors(closeDropdown)}
          >
            <div className={css.menu_inner}>
              <Palette />
              Colors
            </div>
          </Menu>
          <Menu
            disabled={hasNotAvailableAccessBadge}
            notAdvanced={hasNotAvailableAccessBadge}
            {...(hasNotAvailableAccessBadge && {
              onProBadgeClick: handleBadgeClick,
            })}
            onClick={() => handleClickSymbols(closeDropdown)}
          >
            <div className={css.menu_inner}>
              <Symbol />
              Symbols
            </div>
          </Menu>
        </>
      )}
    </DropdownContainer>
  )
}
